import React, { Component } from "react"

/* Poker UI */
import { Chip3D } from "@cuba-go/poker-ui"

import NiuRate from "./NiuRate"

import { utils, sound } from "../constants"
import { BOXES, BET_TYPES, SPOTS, MAX_LOSS, showsLoss, createEmptyBox, boxStaked } from "../constants/values"
import { isMobile } from "../constants/utils"


/* The cloth prints the middle box in Chinese and the outer two in English */
const CHINESE_BOX = 1


/* HOW BIG A CHIP IS DRAWN

   A chip is the same object at every width, so it does not get a size per breakpoint — it
   gets a size per surface it is standing on, and the two phases stand it in two different
   places. While the bets are being taken a phone puts the band aside and gives each box a
   flat strip at the foot of the screen, where a chip has a spot of its own. The moment the
   window shuts the strips go, the band comes back, and from then on the same chips share
   the cloth with three boxes of spots and five cards dealt over each of them.

   Both lines are asked of the browser rather than copied out of the stylesheet: a second
   copy of a breakpoint is wrong the moment either of them moves. They are media queries
   and not the user agent, so a narrow window on a desktop is answered the same way. */
const CHIP_SIZE = 40
const CHIP_SIZE_PHONE = 32

/* Where the betting strips at the foot of the screen are what a bet is placed from */
const PHONE = "(max-width: 600px)"

/* WHERE THE BAND IS DRAWN AT HALF SIZE — and so where a chip standing on it must come down

   niu-rates.css takes the band to scale 0.5 in two places: a phone held upright, and a
   phone on its side. The chips are not part of the band — they are a flat layer laid over
   it, which is the whole point of them, and nothing about the band's scale reaches them.
   So a chip cut for the desktop stood over its spot rather than on it, and on a phone on
   its side it did so at full size, because only the upright half of this was ever asked. */
const SMALL_BAND = "(max-width: 600px), (orientation: landscape) and (max-height: 550px)"

/* A tenth off for the length of the round. Rounded, because the size is the chip's width
   in pixels and the value printed on it is scaled from that. */
const CHIP_SIZE_BAND_SMALL = Math.round(CHIP_SIZE_PHONE * 0.9)


/* THE THREE BOXES OF THE TABLE

   None of them belongs to anyone: every player may put chips on all three at once, and
   each box carries three independent stakes of its own. */
class Boxes extends Component {

    constructor(props) {
        super(props)

        /* Where each spot of the band lands on screen once the cloth has been tilted.
           Empty until the first measurement, and the chip layer draws nothing until then
           rather than piling every chip in the corner. */
        /* The two lines the chip's size answers to, so it changes with them */
        this.media = window.matchMedia(PHONE)
        this.bandMedia = window.matchMedia(SMALL_BAND)

        this.state = { spots: {}, phone: this.media.matches, smallBand: this.bandMedia.matches }

        /* The anchor element sitting at the middle of each spot, keyed box:type */
        this.anchors = {}

        this.frame = null
    }

    componentDidMount = () => {
        this.measure()
        window.addEventListener("resize", this.remeasure)
        this.media.addEventListener("change", this.onWidth)
        this.bandMedia.addEventListener("change", this.onBandWidth)
    }

    componentDidUpdate = () => {
        this.remeasure()
    }

    componentWillUnmount = () => {
        window.removeEventListener("resize", this.remeasure)
        this.media.removeEventListener("change", this.onWidth)
        this.bandMedia.removeEventListener("change", this.onBandWidth)
        if (this.frame) {
            cancelAnimationFrame(this.frame)
        }
    }

    /* The screen crossed the width at which a phone bets from the strips */
    onWidth = event => {
        this.setState({ phone: event.matches })
    }

    /* The band crossed the size at which it is drawn small, so the chips on it follow */
    onBandWidth = event => {
        this.setState({ smallBand: event.matches })
    }

    /* A chip is the size of the thing it is standing on, and the phase says which that is */
    chipSize = () => {

        const { isChoice } = this.props
        const { phone, smallBand } = this.state

        /* Being bet: on a strip of its own at the foot of a phone, or on the desktop band */
        if (isChoice) {
            return phone ? CHIP_SIZE_PHONE : CHIP_SIZE
        }

        /* Being played: on the band, wherever that band is drawn small */
        return smallBand ? CHIP_SIZE_BAND_SMALL : CHIP_SIZE
    }

    /* ===== WHERE THE CHIPS GO =====

       The chips are not part of the cloth. The cloth leans away from the player and
       everything printed on it leans with it; a chip is an object standing on top, and it
       has a picture of its own angle baked in — lean that as well and it flattens into a
       sticker. So the chips are a flat layer laid over the band.

       A flat layer still has to know where the spots ended up, and the answer to that is
       not worth deriving: the band is rotated in perspective, and reproducing that
       projection in arithmetic would be a second copy of the stylesheet, wrong the moment
       either drifts. Instead every spot carries a point of no size at its middle, which
       goes through the same transform as everything else, and the layer simply reads back
       where that point landed. */

    anchor = (box, type, el) => {

        const key = `${box}:${type}`

        if (el) {
            this.anchors[key] = el
        }
        else {
            delete this.anchors[key]
        }
    }

    /* Measure on the next frame. Layout settles after the state that caused it — a box
       dimming, the band swapping in at the end of the betting window — and a measurement
       taken in the same tick reads the position the spot is leaving, not the one it takes. */
    remeasure = () => {

        if (this.frame) return

        this.frame = requestAnimationFrame(() => {
            this.frame = null
            this.measure()
        })
    }

    measure = () => {

        const layer = this._layer

        if (!layer) return

        const origin = layer.getBoundingClientRect()
        const spots = {}

        let moved = false

        Object.keys(this.anchors).forEach(key => {

            const el = this.anchors[key]

            if (!el) return

            const rect = el.getBoundingClientRect()

            /* A zero-size element projects to a point, so this is the centre of the spot
               and not the middle of some bounding box that perspective has skewed */
            const point = { x: rect.left - origin.left, y: rect.top - origin.top }

            spots[key] = point

            const was = this.state.spots[key]

            if (!was || Math.abs(was.x - point.x) > 0.5 || Math.abs(was.y - point.y) > 0.5) {
                moved = true
            }
        })

        if (!moved && Object.keys(spots).length === Object.keys(this.state.spots).length) return

        this.setState({ spots })
    }

    /* What is standing on one spot right now: the stake, or what the round paid it.
       A spot the dealer took is empty — the chips have gone across the line. */
    onSpot = (index, type) => {

        const { boxes, result } = this.props

        const box = boxes && boxes[index] ? boxes[index] : null
        const outcome = result && result[index] && result[index].bets ? result[index].bets[type] : null
        const settled = result && result[index] ? result[index] : null

        if (!box || !box[type]) return null

        if (settled && settled.result === "lose") {

            /* A lost Bet or Double Bet leaves nothing behind at all: the chips going
               across the line is the whole of what happened to it, and a figure over the
               empty spot could only ever repeat it. Only what was charged above its face
               is worth a number — see showsLoss. */
            if (!showsLoss(type)) return null

            return { lost: true, coefficient: outcome ? outcome.coefficient : 0 }
        }

        return {
            stake: box[type],
            win: outcome && outcome.win ? outcome.win : 0,
            coefficient: outcome && outcome.win ? outcome.coefficient : 0
        }
    }

    /* THE CHIP LAYER — flat, above the cloth, untouched by its tilt */
    chipLayer = () => {

        const { chips } = this.props
        const { spots } = this.state

        const nodes = []

        BOXES.forEach(index => {
            SPOTS.forEach(type => {

                const at = spots[`${index}:${type}`]
                const on = this.onSpot(index, type)

                if (!at || !on) return

                nodes.push(
                    <div className="niu-chip" style={{ left: at.x, top: at.y }} key={`chip-${index}-${type}`}>

                        {on.lost ?
                            <div className="niu-chip-badge niu-chip-badge-lost">−{on.coefficient}x</div>
                            :
                            <>
                                <Chip3D value={on.stake} size={this.chipSize()} chips={chips} />
                                {on.win ?
                                    <div className="niu-chip-win">
                                        <div className="niu-chip-badge">{on.coefficient}x</div>
                                        <Chip3D value={on.win} size={this.chipSize()} chips={chips} />
                                    </div>
                                    : null}
                            </>
                        }

                    </div>
                )
            })
        })

        return <div className="niu-chip-layer" ref={el => this._layer = el}>{nodes}</div>
    }

    soundBlock = () => {
        sound.play("blocked")
    }

    add = (type, index) => {

        const {
            setBoxes,
            boxes,
            isChoice,
            active,
            max,
            chips,
            balance,
            toggleBalanceError,
            toggleMaxBetError,
            list,
            setList,
            isClosed
        } = this.props

        /* Bets are only taken while the choice window is open */
        if (!isChoice || isClosed || !boxes || !boxes[index] || !BET_TYPES.includes(type)) {
            this.soundBlock()
            return
        }

        const chipsList = utils.getChips(chips)
        const chip = chipsList.find(c => c.key === active)

        if (!chip) {
            this.soundBlock()
            return
        }

        const box = boxes[index]
        const increment = chip.value
        const value = (box[type] || 0) + increment

        if (value > parseInt(max)) {
            toggleMaxBetError()
            return
        }

        /* The chip costs what the spot holds it at, not its face: a chip on the Super
           Double is taken at fifteen times its value, because that is what it can lose */
        if (balance < increment * MAX_LOSS[type]) {
            toggleBalanceError()
            return
        }

        /* Remember the box as it was, so a single undo can put it back */
        const prev = [{ box: index, stake: { ...box } }]

        const next = boxes.map((item, i) => i === index ? { ...item, [type]: value } : item)

        setBoxes(next)
        setList([...(Array.isArray(list) ? list : []), { boxes: [index], prev }])
    }


    /* The spots repeated at the bottom of a phone screen — one strip per box, all three of
       them, for as long as the window is open. The spots up on the felt are too small to
       hit with a thumb, so these are the only ones a phone player really bets from. */
    mobileRate = index => {

        const { isChoice, boxes, backColor, chips } = this.props

        if (!isChoice) {
            return null
        }

        const box = boxes && boxes[index] ? boxes[index] : createEmptyBox()

        /* The row runs reversed, so box 1 sits on the right of the screen and box 3 on the
           left. The outer two lift clear of the middle one. */
        const style = index === 0 ? "right-rate" : index === BOXES.length - 1 ? "left-rate" : ""

        return (
            <div className={`niu-mobile-rates ${style}`} key={`m-rate-${index}`}>
                <div className="niu-mobile-rates-title">БОКС {index + 1}</div>
                <NiuRate
                    {...box}
                    chinese={index === CHINESE_BOX}
                    inlineChips={true}
                    chipSize={this.chipSize()}
                    chips={chips}
                    isChoice={isChoice}
                    backColor={backColor}
                    add={type => this.add(type, index)}
                    isActive={true}
                />
            </div>
        )
    }


    boxRender = index => {

        const { boxes, isChoice, chips, backColor, result } = this.props

        const box = boxes && boxes[index] ? boxes[index] : createEmptyBox()

        /* How much of the player's attention this box is owed. One he never bought into
           fades back. Nothing dims while the bets are being taken — an empty box is still
           an offer then, and the spot has to look worth putting chips on. */
        const dim = isChoice ? "" : !boxStaked(box) ? "box-idle" : ""

        /* The row runs right to left, so box 1 is the group on the right of the screen.
           The two outer ones are turned in towards the middle — the band curves round the
           table rather than running straight across it. */
        const place = index === 0 ? "is-right" : index === BOXES.length - 1 ? "is-left" : "is-center"

        return (
            <div className={`niu-band-box ${place} ${dim}`} key={`box-${index}`}>
                <NiuRate
                    {...box}
                    result={result && result[index] ? result[index] : null}
                    chinese={index === CHINESE_BOX}
                    chips={chips}
                    isChoice={isChoice}
                    backColor={backColor}
                    add={type => this.add(type, index)}
                    isActive={!isMobile}
                    anchor={(type, el) => this.anchor(index, type, el)}
                />
            </div>
        )
    }

    render = () => {

        const { isChoice, boxes } = this.props

        /* While the bets are being taken a phone gets the flat strips at the foot of the
           screen instead of the band on the cloth — see niu-rates.css. The class is what
           tells the stylesheet which of the two the table is in. */
        const style = isChoice ? "is-choice" : "is-game"

        return (
            <>
                {/* THE BAND ACROSS THE CLOTH

                    One plane, tilted once, carrying everything: three groups of spots with a
                    blue panel in each space between them. The boxes are NOT positioned or
                    turned one by one — that is what made them read as three loose scraps of
                    paper. They are cells of a single row, so they are the same size, sit on
                    the same baseline and lean at the same angle, the way they are painted. */}
                <div className={`niu-band-wrap ${style}`}>
                    <div className="niu-band">
                        {this.boxRender(0)}
                        <div className="niu-band-blue right" />
                        {this.boxRender(1)}
                        <div className="niu-band-blue" />
                        {this.boxRender(2)}
                    </div>
                    {this.chipLayer()}
                </div>
                <div className="bottom-rates-mobile">
                    {boxes && Array.isArray(boxes) ? BOXES.map(index => this.mobileRate(index)) : null}
                </div>
            </>
        )

    }

}

export default Boxes
